import BaseAPI from '../baseApi';
import { configuration } from '../../configuration';
import {
  AddressComponent,
  AddressDetails,
  PlaceDetailsResult,
  Prediction,
  Predictions,
} from '../interfaces/maps';

export class MapsApi extends BaseAPI {
  protected prefix = 'maps/api/place';

  get host(): string {
    return configuration.mapsUrl;
  }

  async getCaptchaToken(): Promise<string> {
    const maxRetries = 100;
    let retries = 0;

    return new Promise((resolve, reject) => {
      const interval = setInterval(() => {
        if (window.grecaptcha?.enterprise?.execute) {
          clearInterval(interval);
          const recaptcha =
            window.grecaptcha.enterprise.execute(configuration.recaptchaV3SiteKey, {
              action: 'submit',
            }) || '';

          resolve(recaptcha);
        } else {
          retries++;
          if (retries > maxRetries) {
            clearInterval(interval);
            reject('Recaptcha not loaded');
          }
        }
      }, 100);
    });
  }

  async getPlacePredictions(input): Promise<Array<Prediction>> {
    const reCaptchaToken = await this.getCaptchaToken();

    const { predictions } = await this.get<Predictions>('autocomplete/json', {
      input,
      re_captcha_token: reCaptchaToken,
      components: 'country:us',
    });

    return predictions.map(({ description, placeId }) => ({ description, placeId }));
  }

  async getPlaceDetails(placeId: string): Promise<AddressDetails> {
    const reCaptchaToken = await this.getCaptchaToken();
    const params = new URLSearchParams({
      placeid: placeId,
      re_captcha_token: reCaptchaToken,
    });

    const { result } = await this.get<PlaceDetailsResult>(`details/json?${params}`);
    const { formattedAddress, addressComponents, geometry, placeId: resultPlaceId } = result;
    const componentNames = [
      'street_number',
      'premise',
      'route',
      'sublocality',
      'locality',
      'administrative_area_level_1',
      'postal_code',
    ];

    const address = componentNames.reduce((acc, componentName) => {
      acc[componentName] = addressComponents.find(({ types }) => types.includes(componentName));
      return acc;
    }, {} as Record<string, AddressComponent>);

    if (!address.street_number) {
      address.street_number = address.premise;
    }

    address.city = address.sublocality || address.locality;

    return {
      formattedAddress: formattedAddress,
      address1: [address?.street_number?.longName, address?.route?.longName].join(' '),
      streetNumber: address?.street_number?.longName,
      city: address?.city?.longName,
      latitude: geometry.location.lat,
      longitude: geometry.location.lng,
      state: address?.administrative_area_level_1?.shortName,
      zipCode: address?.postal_code?.longName,
      placeId: resultPlaceId,
    } as AddressDetails;
  }
}
